"use client";

import * as React from "react";
import { BookOpen, Check, ChevronDown, GraduationCap } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CanvasCourse } from "@/lib/types";

interface CreateNotebookModalProps {
  open: boolean;
  onCancel: () => void;
  allowClickAway: boolean;
  courses: CanvasCourse[];
  onConfirm: (data: { name: string; course: CanvasCourse }) => void;
}

export function CreateNotebookModal({
  open,
  onCancel,
  courses,
  onConfirm,
  allowClickAway,
}: CreateNotebookModalProps) {
  const [name, setName] = React.useState("");
  const [selectedCourse, setSelectedCourse] =
    React.useState<CanvasCourse | null>(null);

  const MAX_VISIBLE_COURSES = 6;
  const visibleCourses = courses.slice(0, MAX_VISIBLE_COURSES - 1);
  const hasOverflow = courses.length > MAX_VISIBLE_COURSES;
  const overflowCourses = courses.slice(MAX_VISIBLE_COURSES - 1);

  const reset = () => {
    setName("");
    setSelectedCourse(null);
  };

  const handleConfirm = () => {
    if (!selectedCourse) return;
    onConfirm({ name: "Untitled Study Set", course: selectedCourse });
    reset();
  };

  const disabled = !selectedCourse;

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        if (allowClickAway) {
          setSelectedCourse(null);
          onCancel();
        }
      }}
    >
      <DialogContent className="animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200">
        {/* Header */}
        <div className="pb-6 border-b-2 border-theme">
          <div className="flex items-start gap-4">
            {/* <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient shadow-lg"> */}
            {/* <GraduationCap className="h-7 w-7 text-white" /> */}
            {/* </div> */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-theme-0 tracking-tight">
                Create Study Set
              </h2>
              <p className="text-sm text-theme-1 mt-1.5 font-medium">
                Choose the course you want to study for
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="py-4">
          <div className="space-y-3">
            <label className="text-sm font-bold text-theme-0 flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Select Course
            </label>

            <div className="grid grid-cols-3 gap-1">
              {visibleCourses.map((course) => {
                const isSelected = selectedCourse?.id === course.id;

                return (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => setSelectedCourse(course)}
                    className={cn(
                      "relative rounded-xl transition-all duration-200 group",
                      "hover:scale-[1.02] active:scale-[0.98]",
                      isSelected && "shadow-lg hover:shadow-lg"
                    )}
                  >
                    <div
                      className={cn(
                        "w-full h-full px-4 py-8 relative rounded-xl overflow-hidden border-2"
                      )}
                      style={{
                        borderColor: isSelected ? course.color : "transparent",
                      }}
                    >
                      {/* Gradient overlay */}
                      <div
                        className="absolute inset-0 opacity-20"
                        style={{
                          backgroundColor: course.color,
                          backgroundImage: `url(${course.img})` || "none",
                        }}
                      />

                      {/* Content */}
                      <div className="relative z-10 flex flex-col items-center gap-3">
                        <span className="line-clamp-2 text-sm font-bold text-theme-0 text-center leading-tight">
                          {course.code || course.default}
                        </span>
                      </div>

                      {/* Check indicator */}
                      {isSelected && (
                        <div
                          className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center shadow-md"
                          style={{ backgroundColor: course.color }}
                        >
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Overflow selector */}
              {hasOverflow && (
                <div
                  className={cn(
                    "rounded-xl border-2 transition-all duration-200 hover:border-theme hover:bg-theme-1 flex items-center justify-center",
                    selectedCourse &&
                      courses.indexOf(selectedCourse) >= MAX_VISIBLE_COURSES - 1
                      ? "shadow-lg"
                      : "hover:shadow-lg border-dashed"
                  )}
                  style={{
                    borderColor:
                      selectedCourse &&
                      courses.indexOf(selectedCourse) >= MAX_VISIBLE_COURSES - 1
                        ? selectedCourse.color
                        : "#cbd5e1",
                  }}
                >
                  <Select
                    value={
                      courses.indexOf(selectedCourse) >= MAX_VISIBLE_COURSES - 1
                        ? selectedCourse?.id.toString() ?? ""
                        : undefined
                    }
                    onValueChange={(id) =>
                      setSelectedCourse(
                        courses.find((c) => c.id.toString() === id) ?? null
                      )
                    }
                  >
                    <SelectTrigger
                      className="h-full w-full border-none py-10 rounded-xl shadow-none flex items-center justify-center"
                      withArrow={false}
                    >
                      <div className="flex flex-row items-center gap-2">
                        <SelectValue placeholder="Other course" />
                        <ChevronDown className="h-5 w-5 text-theme-1" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {overflowCourses.map((course) => (
                        <SelectItem
                          key={course.id}
                          value={course.id.toString()}
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className="h-3 w-3 rounded-full shadow-sm"
                              style={{
                                backgroundColor: course.color || "#6366f1",
                              }}
                            />
                            <span className="font-medium text-theme-0">
                              {course.code || course.default}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between pt-2">
          <Button
            className="rounded-full shadow-none"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={disabled}
            className="bg-brand-gradient text-theme-0 rounded-full text-white"
          >
            Create Study Set
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
